import React, { useEffect, useState } from 'react';
import { Utensils, ShoppingBag, Bike, MapPin } from 'lucide-react';

const FEATURES = [
  { Icon: Utensils, label: 'Food' },
  { Icon: ShoppingBag, label: 'Products' },
  { Icon: Bike, label: 'Delivery' },
  { Icon: MapPin, label: 'Tracking' },
];

// Animated splash shown over the app on first load, mirroring the iOS
// SplashScreenView. The static splash in index.html covers the pre-React
// gap; this takes over, plays the intro, then fades out.
export default function SplashScreen({ onFinished, ready = true }) {
  const [exiting, setExiting] = useState(false);
  const startRef = React.useRef(Date.now());

  // Fade out once the splash has been up for its minimum beat AND the app
  // reports ready — keeps the brand screen up instead of flashing a white
  // loading state while auth is still resolving.
  useEffect(() => {
    if (!ready || exiting) return;
    const wait = Math.max(0, 2000 - (Date.now() - startRef.current));
    const t = setTimeout(() => setExiting(true), wait);
    return () => clearTimeout(t);
  }, [ready, exiting]);

  useEffect(() => {
    if (!exiting) return;
    const t = setTimeout(() => onFinished?.(), 500);
    return () => clearTimeout(t);
  }, [exiting, onFinished]);

  return (
    <div
      className="chef-splash"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #a60d0d 0%, #d91f1f 55%, #ff3b30 100%)',
        opacity: exiting ? 0 : 1,
        transition: 'opacity 0.5s ease-out',
        pointerEvents: exiting ? 'none' : 'auto',
      }}
    >
      <img
        src="/ChefLogo.jpg"
        alt="CheF SKN"
        className="chef-splash-logo"
        style={{
          width: 150,
          height: 150,
          borderRadius: '50%',
          objectFit: 'cover',
          boxShadow: '0 10px 18px rgba(0,0,0,0.25)',
        }}
      />
      <div
        className="chef-splash-text"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 22 }}
      >
        <span
          style={{
            color: '#fff',
            fontSize: 46,
            fontWeight: 800,
            fontFamily: 'Inter, system-ui, sans-serif',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          CheF
        </span>
        <span
          style={{
            marginTop: 6,
            color: 'rgba(255,255,255,0.95)',
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: 3,
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          ST. KITTS &amp; NEVIS
        </span>
        <span
          style={{
            marginTop: 10,
            color: 'rgba(255,255,255,0.85)',
            fontSize: 15,
            fontWeight: 500,
            fontStyle: 'italic',
            fontFamily: 'Inter, system-ui, sans-serif',
          }}
        >
          Delicious Food &amp; Products, Delivered
        </span>
      </div>
      <div
        className="chef-splash-features"
        style={{ display: 'flex', gap: 28, marginTop: 34 }}
      >
        {FEATURES.map(({ Icon, label }, i) => (
          <div
            key={label}
            className={i === 2 ? 'chef-splash-bob' : undefined}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 7,
              color: 'rgba(255,255,255,0.95)',
            }}
          >
            <Icon size={20} strokeWidth={2.2} />
            <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'Inter, system-ui, sans-serif' }}>
              {label}
            </span>
          </div>
        ))}
      </div>
      <style>{`
        .chef-splash-logo { animation: chefSplashPop .6s cubic-bezier(.34,1.56,.64,1) both; }
        .chef-splash-text { animation: chefSplashUp .5s ease-out .35s both; }
        .chef-splash-features { animation: chefSplashUp .45s ease-out .65s both; }
        .chef-splash-bob { animation: chefSplashBob .8s ease-in-out .9s infinite alternate; }
        @keyframes chefSplashPop { from { transform: scale(.82); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        @keyframes chefSplashUp { from { transform: translateY(12px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        @keyframes chefSplashBob { from { transform: translateY(0) } to { transform: translateY(-4px) } }
      `}</style>
    </div>
  );
}
